import React, { useState } from 'react'
import styled from 'styled-components'
import { ImageIcon } from '~/components/ImageIcon'
import { dimensions, light } from '~/constants/theme'

import Text, { FontStyle } from './Text'
import Tooltip from './Tooltip'

interface StyledButtonProps {
  cursor?: string
  width?: string | number
  height?: string | number
  noPadding?: boolean
  align?: string
  borderRadius?: string | number
  borderColor?: string
  borderWidth?: string | number
  color?: string
  disabled?: boolean
}

const StyledButton = styled.div<StyledButtonProps>`
  display: flex;
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  padding: ${(props) => (props.noPadding ? '0px' : '0 20px')};
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.align};
  border-radius: ${(props) =>
    props.borderRadius ? props.borderRadius : dimensions.borderRadiusLG};
  border-color: ${(props) => props.borderColor};
  border-width: ${(props) => props.borderWidth}px;
  border-style: solid;
  box-sizing: border-box;
  background-color: ${(props) => props.color};
  transition: all 0.2s ease-in-out;
  cursor: ${(props) =>
    props.disabled ? 'default' : (props.cursor ?? 'pointer')};

  > p {
    margin-right: ${(props) => (props.noPadding ? '8px' : '0px')};
    margin-left: 8px;
  }
`

interface ImageButtonProps {
  children?: React.ReactNode
  icon?: SVGAsset
  activeIcon?: SVGAsset
  disabledIcon?: SVGAsset
  hoverIcon?: SVGAsset
  disabled?: boolean
  title?: string
  cursor?: string
  align?: string
  width?: string | number
  height?: string | number
  imgWidth?: string | number
  imgHeight?: string | number
  borderColor?: string
  borderWidth?: string | number
  borderRadius?: string | number
  tooltipText?: string
  textColor?: string
  textHoverColor?: string
  textActiveColor?: string
  textDisabledColor?: string
  backColor?: string
  hoveredColor?: string
  activeColor?: string
  disabledColor?: string
  style?: React.CSSProperties
  noPadding?: boolean
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}

const ImageButton = ({
  children,
  disabled = false,
  title,
  icon,
  cursor = 'pointer',
  hoverIcon,
  activeIcon,
  disabledIcon,
  align = 'center',
  imgWidth = '16',
  width = '96px',
  imgHeight = '17',
  height = '36px',
  backColor = light.primary,
  hoveredColor = light.primaryHover,
  activeColor = light.primary,
  disabledColor = `${light.gray400}`,
  borderColor = light.transparent,
  borderWidth = 0,
  tooltipText = '',
  textColor = light.gray100,
  textHoverColor = light.gray100,
  textActiveColor = light.gray200,
  textDisabledColor = light.gray100,
  onClick,
  ...rest
}: ImageButtonProps) => {
  const [hovered, setHovered] = useState(false)
  const [clicked, setClicked] = useState(false)

  const handleEntered = () => {
    setHovered(true)
  }

  const handleLeave = () => {
    setHovered(false)
  }

  const handleMouseDown = () => {
    setClicked(true)
  }

  const handleMouseUp = () => {
    setClicked(false)
  }

  let color = backColor
  let tColor = textColor
  let resolvedIcon = icon
  if (hovered) {
    color = hoveredColor
    tColor = textHoverColor
    resolvedIcon = hoverIcon ?? icon
  }
  if (clicked) {
    color = activeColor
    tColor = textActiveColor
    resolvedIcon = activeIcon ?? icon
  }
  if (disabled) {
    color = disabledColor
    tColor = textDisabledColor
    resolvedIcon = disabledIcon ?? icon
  }

  let resolvedNoPadding = rest.noPadding !== undefined ? rest.noPadding : false
  if (!resolvedNoPadding && (!icon || !title)) {
    resolvedNoPadding = true
  }

  //remove later
  const isLegacyIcon = (resolvedIcon?.split('/').length ?? 0) <= 1
  return (
    <StyledButton
      cursor={cursor}
      width={width}
      height={height}
      color={color}
      align={align}
      disabled={disabled}
      noPadding={resolvedNoPadding}
      borderColor={borderColor}
      borderWidth={borderWidth}
      data-for={tooltipText}
      data-tip={tooltipText}
      onClick={(e) => !disabled && onClick && onClick(e)}
      onMouseEnter={handleEntered}
      onMouseLeave={handleLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      {...rest}
    >
      {children ?? (
        <>
          {resolvedIcon && (
            <ImageIcon
              width={imgWidth}
              height={imgHeight}
              icon={isLegacyIcon ? undefined : resolvedIcon}
              url={isLegacyIcon ? resolvedIcon : undefined}
            />
          )}
          {tooltipText && <Tooltip id={tooltipText} />}
          {title && (
            <Text
              fontStyle={FontStyle.Small}
              color={tColor}
              text={title}
              weight="600"
            />
          )}
        </>
      )}
    </StyledButton>
  )
}

export default ImageButton
